import React, { useEffect } from "react";
import { BrowserRouter, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import InAppMessageOverlay from "@/components/InAppMessageOverlay";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import HotelNotificationListener from "@/components/HotelNotificationListener";
import DriverNotificationListener from "@/components/DriverNotificationListener";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import PriceOnboarding from "@/components/price/PriceOnboarding";
import AccountSuspendedScreen from "@/components/price/AccountSuspendedScreen";
import CguGate from "@/components/price/CguGate";
import LiveDecorations from "@/components/price/DecorationsLayer";
import PendingFormPopup from "@/components/price/PendingFormPopup";
import QuestionnairePage from "@/pages/QuestionnairePage";
import { initNativePush, isNativeApp } from "@/lib/nativePush";

import Landing from "@/pages/Landing";
import PriceHome from "@/pages/price-site/PriceHome";
import PriceDownload from "@/pages/price-site/PriceDownload";
import PriceComingSoon from "@/pages/price-site/PriceComingSoon";
import PricePrivacy from "@/pages/price-site/PricePrivacy";
import PriceCGU from "@/pages/price-site/PriceCGU";
import {
  PriceVerifyEmailHandler,
  PriceEmailVerifiedPage,
  PriceEmailAlreadyVerifiedPage,
  PriceEmailExpiredPage,
  PriceEmailErrorPage,
} from "@/pages/price-site/PriceVerifyEmail";
import { PriceDepositLanding, PriceDepositSuccess } from "@/pages/price-site/PriceDepositLanding";
import HotelRegister from "@/pages/HotelRegister";
import DriverRegister from "@/pages/DriverRegister";
import BecomeDriver from "@/pages/BecomeDriver";
import Login from "@/pages/Login";
import HotelDashboard from "@/pages/HotelDashboard";
import HotelHistory from "@/pages/HotelHistory";
import HotelMultiDestination from "@/pages/HotelMultiDestination";
import DriverDashboard from "@/pages/DriverDashboard";
import DriverHistory from "@/pages/DriverHistory";
import DriverActiveRide from "@/pages/DriverActiveRide";
import DriverQuotes from "@/pages/DriverQuotes";
import AdminDashboard from "@/pages/AdminDashboard";
import TrackingPage from "@/pages/TrackingPage";
import ChatOnlyPage from "@/pages/ChatOnlyPage";
import PaymentPage from "@/pages/PaymentPage";
import PaymentReturn from "@/pages/PaymentReturn";
import PricePaymentReturn from "@/pages/PricePaymentReturn";
import PriceStripeConnectReturn from "@/pages/PriceStripeConnectReturn";
import Pay from "@/pages/Pay";
import SquarePosCallback from "@/pages/SquarePosCallback";
import Debug from "@/pages/Debug";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import RegisterChoice from "@/pages/RegisterChoice";
import AirbnbRegister from "@/pages/AirbnbRegister";
import ClientRegister from "@/pages/ClientRegister";
import PriceVerifyIdentity from "@/pages/PriceVerifyIdentity";
import ClientDashboard from "@/pages/ClientDashboard";
import ClientPaymentMethods from "@/pages/ClientPaymentMethods";
import ClientHistory from "@/pages/ClientHistory";
import PublicHotelBooking from "@/pages/PublicHotelBooking";
import RouteTransition from "@/components/RouteTransition";

import "@/App.css";

function Shell({ children, hideChrome }) {
  const { user, profile } = useAuth();
  const hotelId = user?.role === "hotel" ? profile?.id : null;
  const driverUserId = user?.role === "driver" ? user?.id : null;
  // Inside the Capacitor wrapper we render the public landing AND all auth
  // flows (login/register/*) full-bleed, without the marketing Navbar/Footer
  // chrome — they each ship their own native Price frame.
  const path = window.location.pathname;
  const isAuthPath =
    path === "/" ||
    path === "/welcome" ||
    path === "/login" ||
    path.startsWith("/register");
  const hideForNative = isNativeApp() && !user && isAuthPath;
  // Price is app-first — no marketing website. When an anonymous visitor
  // hits the landing / auth screens we hide the top marketing chrome on
  // both web AND native so the entry always feels like a mobile app.
  const hideChromeForAnonEntry = !user && isAuthPath;
  if (hideChrome || hideForNative || hideChromeForAnonEntry) return <div className="App">{children}</div>;
  // Inside the Capacitor mobile app (iOS/Android) we hide the marketing
  // Footer entirely — it lists legal / contact / SEO copy that only makes
  // sense on the web landing. Web visitors still see the full Footer.
  const showFooter = !isNativeApp() && !user;
  // Hide the global marketing Navbar on the role dashboards — each of
  // them already ships its own profile menu (ClientProfileMenu /
  // DriverProfileMenu / HotelProfileMenu). Keeping both would double
  // the burger icon and confuse the user (they'd see a burger next to
  // "Compte validé" AND another one at the very top).
  const isRoleDashboard = /^\/(admin|dashboard\/(client|driver|hotel))(\/|$)/.test(path);
  return (
    <div className="App flex min-h-screen flex-col bg-background">
      {!isRoleDashboard && <Navbar />}
      <main className="flex-1">{children}</main>
      {showFooter && <Footer />}
      {hotelId && <HotelNotificationListener hotelId={hotelId} />}
      {driverUserId && <DriverNotificationListener driverUserId={driverUserId} />}
      <PWAInstallPrompt />
    </div>
  );
}

/**
 * PushNavigator — écoute l'event `price:push:navigate` émis par
 * `nativePush.js` quand l'utilisateur tape une notification OneSignal.
 * Utilise React Router (`useNavigate`) pour router côté client SANS
 * hard-reload — l'état SPA (AuthContext, course active, chat) est
 * intégralement préservé. Ce composant doit être monté A L'INTERIEUR
 * d'un <BrowserRouter> (sinon `useNavigate` throw).
 *
 * Cold-boot safety : `nativePush.js` bufferise le path dans
 * `window.__PRICE_PENDING_PUSH_PATH__` — au mount on draine ce buffer
 * si l'event a été dispatché avant que la SPA soit prête (App.js
 * initialise OneSignal APRES le premier render).
 */
function PushNavigator() {
  const navigate = useNavigate();
  useEffect(() => {
    const consume = (path) => {
      if (!path) return;
      try {
        window.__PRICE_PENDING_PUSH_PATH__ = null;
        navigate(path, { replace: false });
      } catch (_e) {
        window.location.href = path;
      }
    };
    const handler = (e) => consume(e?.detail?.path || window.__PRICE_PENDING_PUSH_PATH__);
    window.addEventListener("price:push:navigate", handler);
    // Drain any buffered path that arrived before this listener mounted
    // (case : app cold-booted from a push tap → click fired at t=0 mais
    // la SPA + React Router n'étaient pas encore prêts).
    if (window.__PRICE_PENDING_PUSH_PATH__) consume(window.__PRICE_PENDING_PUSH_PATH__);
    return () => window.removeEventListener("price:push:navigate", handler);
  }, [navigate]);
  return null;
}

/**
 * Détecte si on est servi sur le domaine du site officiel Price
 * (pricecovoit.fr, www.pricecovoit.fr) OU si l'URL contient le flag
 * `?site=price` (utile pour tester le rendu marketing sur preview).
 *
 * Quand true, l'app frontale ne monte QUE les routes du site vitrine
 * (Home, /download, /coming-soon, /confidentialite, /cgu). L'app
 * mobile (login, dashboards, admin) reste 100% invisible côté web.
 */
function isPriceMarketingHost() {
  if (typeof window === "undefined") return false;
  // ⚠️  Dans l'app native Capacitor (iOS/Android), on NE DOIT JAMAIS
  // activer le mode marketing — même si Capacitor configure
  // `window.location.hostname === "pricecovoit.fr"` via server.hostname
  // dans capacitor.config.json. L'app doit toujours démarrer sur le
  // vrai routeur applicatif (login, welcome, dashboards, admin). Le
  // mode marketing est RÉSERVÉ au navigateur web pointant sur
  // pricecovoit.fr.
  if (isNativeApp()) return false;
  const host = String(window.location.hostname || "").toLowerCase();
  const path = String(window.location.pathname || "");
  // Backdoor : certaines routes app doivent toujours monter l'application
  // complète, même depuis pricecovoit.fr :
  //  - /admin, /login : accès admin et connexion utilisateur
  //  - /welcome       : écran de sélection Passager/Covoitureur (landing app)
  //  - /register/*    : inscription (web) — utile Android tant que le
  //                     Play Store n'a pas la version native
  //  - /dashboard/*   : dashboards passager/covoit une fois connectés
  //  - /activate/*    : bascule de rôle
  if (/^\/(admin|login|welcome|register|dashboard|activate|verify-identity|deposit)(\/|$)/.test(path)) return false;
  if (/^(www\.)?pricecovoit\.fr$/i.test(host)) return true;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("site") === "price") return true;
  } catch (_e) { /* noop */ }
  return false;
}

export default function App() {
  // Capture ?ref=CODE au premier chargement — persistant en sessionStorage
  // pour survivre à la navigation vers le formulaire d'inscription (le
  // paramètre est stocké UNE fois puis nettoyé au succès de l'inscription).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get("ref") || "").trim().toUpperCase();
      if (ref && /^[A-Z0-9]{4,10}$/.test(ref)) {
        sessionStorage.setItem("price_referral_code", ref);
      }
    } catch (_e) { /* noop */ }
  }, []);

  // Native (Capacitor) bootstrap — runs once at app launch on Android/iOS.
  // On web/PWA this is a no-op; the OneSignal Web SDK still handles push.
  useEffect(() => {
    if (!isNativeApp()) return;
    // `deviceready` fires when the Cordova plugins are loaded inside the
    // Capacitor wrapper. We must wait for it before calling OneSignal.
    const start = () => { initNativePush(); };
    if (window.cordova) {
      document.addEventListener("deviceready", start, { once: true });
      // Some Capacitor versions skip the `deviceready` event entirely —
      // try after a short delay too as a safety net.
      setTimeout(start, 1500);
    } else {
      // Pure-Capacitor (no Cordova bridge) — try immediately.
      start();
    }
  }, []);

  // Site officiel pricecovoit.fr — 100% marketing, aucune app web accessible.
  if (isPriceMarketingHost()) {
    return (
      <BrowserRouter>
        <ScrollToTop />
        <RouteTransition>
          <Route path="/" element={<PriceHome />} />
          <Route path="/download" element={<PriceDownload />} />
          <Route path="/q/:token" element={<QuestionnairePage />} />
          <Route path="/coming-soon" element={<PriceComingSoon />} />
          <Route path="/confidentialite" element={<PricePrivacy />} />
          <Route path="/cgu" element={<PriceCGU />} />
          {/* Email verification pages — accessibles depuis n'importe où
              car l'utilisateur arrive via un lien reçu par email. */}
          <Route path="/verify-email" element={<PriceVerifyEmailHandler />} />
          <Route path="/email-verifie" element={<PriceEmailVerifiedPage />} />
          <Route path="/email-deja-verifie" element={<PriceEmailAlreadyVerifiedPage />} />
          <Route path="/verification-expiree" element={<PriceEmailExpiredPage />} />
          <Route path="/verification-erreur" element={<PriceEmailErrorPage />} />
          {/* Stripe deposit landing (caution) — accessible via token
              partagé par l'admin. Pas d'auth requise. */}
          <Route path="/deposit/:token" element={<PriceDepositLanding />} />
          <Route path="/deposit/:token/success" element={<PriceDepositSuccess />} />
          {/* Retour Stripe Connect onboarding — Stripe redirige ici
              après que le covoitureur a (ou pas) terminé son KYC. */}
          <Route path="/stripe-connect-return" element={<PriceStripeConnectReturn />} />
          {/* Retour post-paiement Stripe Checkout (course covoiturage).
              Essaie d'ouvrir l'app via deep link `com.price.covoiturage://`
              puis fallback /download. Route accessible depuis
              pricecovoit.fr pour les paiements web standalone. */}
          <Route path="/payment-return" element={<PricePaymentReturn />} />
          {/* Toute autre route web (login, dashboard, admin, etc.) est
              silencieusement redirigée vers l'accueil marketing — l'app
              n'est PAS accessible sur web, uniquement via iOS / Android. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </RouteTransition>
        <Toaster richColors closeButton position="top-center" />
      </BrowserRouter>
    );
  }

  return (
    <AuthProvider>
      <LangProvider>
      <BrowserRouter>
        <ScrollToTop />
        <PushNavigator />
        <RouteTransition>
          <Route path="/" element={<Shell><Landing /></Shell>} />
          <Route path="/welcome" element={<Shell><Landing /></Shell>} />
          <Route path="/register" element={<Shell><RegisterChoice /></Shell>} />
          <Route path="/register/hotel" element={<Shell><HotelRegister /></Shell>} />
          <Route path="/register/airbnb" element={<Shell><AirbnbRegister /></Shell>} />
          <Route path="/register/client" element={<Shell><ClientRegister /></Shell>} />
          {/* Hotel public booking widget — zero-account guest flow */}
          <Route path="/h/:slug" element={<Shell><PublicHotelBooking /></Shell>} />
          {/* Promo discount link — same widget, overlays admin-configured % */}
          <Route path="/p/:slug" element={<Shell><PublicHotelBooking /></Shell>} />
          <Route path="/register/driver" element={<Shell><DriverRegister /></Shell>} />
          {/* Dual-role : un passager connecté crée son profil conducteur */}
          <Route path="/become-driver" element={
            <ProtectedRoute roles={["client"]}>
              <BecomeDriver />
            </ProtectedRoute>
          } />
          <Route path="/login" element={<Shell><Login /></Shell>} />

          {/* Email verification — accessibles depuis n'importe où (l'user
              arrive via un lien reçu par email). Pas de Shell : les pages
              ont leur propre layout (PriceSiteLayout). */}
          <Route path="/verify-email" element={<PriceVerifyEmailHandler />} />
          <Route path="/email-verifie" element={<PriceEmailVerifiedPage />} />
          <Route path="/email-deja-verifie" element={<PriceEmailAlreadyVerifiedPage />} />
          <Route path="/verification-expiree" element={<PriceEmailExpiredPage />} />
          <Route path="/verification-erreur" element={<PriceEmailErrorPage />} />
          {/* Stripe deposit landing (caution) — accessible via token
              partagé par l'admin. Pas d'auth requise. */}
          <Route path="/deposit/:token" element={<PriceDepositLanding />} />
          <Route path="/deposit/:token/success" element={<PriceDepositSuccess />} />
          <Route path="/verify-identity" element={<PriceVerifyIdentity />} />

          <Route path="/dashboard/hotel" element={
            <ProtectedRoute roles={["hotel"]}>
              <Shell><HotelDashboard /></Shell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/client" element={
            <ProtectedRoute roles={["client"]}>
              <ClientDashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/client/payment-methods" element={
            <ProtectedRoute roles={["client"]}>
              <ClientPaymentMethods />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/client/history" element={
            <ProtectedRoute roles={["client"]}>
              <ClientHistory />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/hotel/history" element={
            <ProtectedRoute roles={["hotel"]}>
              <Shell><HotelHistory /></Shell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/hotel/multi-destination" element={
            <ProtectedRoute roles={["hotel"]}>
              <Shell><HotelMultiDestination /></Shell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/hotel/pay/:bookingId" element={
            <ProtectedRoute roles={["hotel"]}>
              <Shell><PaymentPage /></Shell>
            </ProtectedRoute>
          } />
          <Route path="/payment/return" element={
            <ProtectedRoute roles={["hotel"]}>
              <Shell><PaymentReturn /></Shell>
            </ProtectedRoute>
          } />
          {/* Price — retour post-paiement d'une course covoiturage.
              Route PUBLIQUE (pas d'auth) : la session Stripe peut ouvrir
              cette page dans un navigateur anonyme (SFSafariView n'a
              pas les cookies de l'app). On ferme simplement le browser
              pour revenir dans l'app Capacitor, ou on redirige vers le
              dashboard si consulté sur navigateur web standard. */}
          <Route path="/payment-return" element={<PricePaymentReturn />} />
          {/* Retour Stripe Connect onboarding — route PUBLIQUE pour
              gérer le cas où le browser Capacitor n'arrive pas à se
              refermer tout seul. */}
          <Route path="/stripe-connect-return" element={<PriceStripeConnectReturn />} />
          <Route path="/debug" element={<Shell><Debug /></Shell>} />
          <Route path="/dashboard/driver" element={
            <ProtectedRoute roles={["driver"]}>
              <Shell><DriverDashboard /></Shell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/driver/active" element={
            <ProtectedRoute roles={["driver"]}>
              <Shell><DriverActiveRide /></Shell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/driver/history" element={
            <ProtectedRoute roles={["driver"]}>
              <Shell><DriverHistory /></Shell>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/driver/quotes" element={
            <ProtectedRoute roles={["driver"]}>
              <Shell><DriverQuotes /></Shell>
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute roles={["admin"]}>
              <Shell><AdminDashboard /></Shell>
            </ProtectedRoute>
          } />

          <Route path="/track/:token" element={<Shell hideChrome><TrackingPage /></Shell>} />
          <Route path="/chat/:token" element={<Shell hideChrome><ChatOnlyPage /></Shell>} />
          <Route path="/pay/:bookingId" element={<Pay />} />
          <Route path="/square/pos-callback" element={<SquarePosCallback />} />
          <Route path="/square/pos-fallback" element={<SquarePosCallback />} />
          <Route path="/privacy" element={<Shell><Privacy /></Shell>} />
          <Route path="/confidentialite" element={<Shell><Privacy /></Shell>} />
          <Route path="/cgu" element={<Shell><Terms /></Shell>} />
          <Route path="/terms" element={<Shell><Terms /></Shell>} />
          <Route path="/q/:token" element={<QuestionnairePage />} />

          <Route path="*" element={<Shell><Landing /></Shell>} />
        </RouteTransition>
        <LiveDecorations />
        <PendingFormPopup />
        <CguGate />
      </BrowserRouter>
      {/* Toaster — style Price cartoon/street.
          Le vrai styling vit dans `index.css` (voir `[data-sonner-toast]`)
          pour pouvoir cibler chaque variant via `[data-type=...]`. Ici on
          garde juste la position, l'offset safe-area et la durée. */}
      <Toaster
        position="top-center"
        offset={20}
        mobileOffset={20}
        gap={12}
        toastOptions={{
          duration: 3500,
        }}
      />
      <InAppMessageOverlay />
      <PriceOnboarding />
      <AccountSuspendedScreen />
      </LangProvider>
    </AuthProvider>
  );
}
